import { setInsuranseData } from 'redux/store/slices/decisionSlice'
import type {
  AddSpousePayload,
  AddSpouseResponse,
  EstimateRealEstateResponse,
  EstimateRealEstateRestartResponse,
  GetClientPropertiesPayload,
  GetClientPropertiesResponse,
  GetCreditApplicationInfoResponse,
  GetDocsForPledgeResponse,
  GetInsuranceSumPayload,
  GetInsuranceSumResponse,
  GetNotariesResponse,
  GetNotaryCitiesResponse,
  GetSignDocsToStepResponse,
  SetNotaryPayload,
  SetNotaryResponse,
  SignDocsToStepPayload,
  SignDocsToStepResponse,
  StartPayboxComissionPaymentResponse
} from 'share/types/api/pledge'
import { notaryUrl } from 'utils/constants'

import { api } from '.'

const pledgeApi = api.injectEndpoints({
  endpoints: build => ({
    getClientProperties: build.query<GetClientPropertiesResponse, GetClientPropertiesPayload>({
      query: credit_id => ({
        url: `/credits/${credit_id}/property-pledges/`,
        method: 'get'
      })
    }),
    fetchClientProperties: build.mutation<GetClientPropertiesResponse, GetClientPropertiesPayload>({
      query: credit_id => ({
        url: `/credits/${credit_id}/property-pledges/`,
        method: 'get'
      })
    }),
    getNotaryCities: build.query<GetNotaryCitiesResponse, void>({
      query: () => ({
        url: `${notaryUrl}/address/`
      })
    }),
    getNotaries: build.query<GetNotariesResponse, Record<string, any>>({
      query: params => ({
        url: `${notaryUrl}/notaries/`,
        params
      })
    }),
    setNotary: build.mutation<SetNotaryResponse, SetNotaryPayload>({
      query: ({ notary_id, credit_id }) => ({
        url: `/scoring/choosing-notary/${credit_id}/`,
        method: 'post',
        body: {
          notary_id
        }
      })
    }),
    startCheckingDocuments: build.mutation<void, string>({
      query: flow_id => ({
        url: `/scoring/checking_documents/${flow_id}/`,
        method: 'post'
      })
    }),
    getDocsForPledge: build.query<GetDocsForPledgeResponse, string>({
      query: flow_id => ({
        url: `/scoring/checking_documents/${flow_id}/`
      })
    }),
    getCreditApplicationInfo: build.query<GetCreditApplicationInfoResponse, string>({
      query: credit_id => ({
        url: `/credits/credit-application-info/${credit_id}`,
        method: 'get'
      })
    }),
    startPayboxComissionPayment: build.mutation<StartPayboxComissionPaymentResponse, string>({
      query: step_id => ({
        url: `/payments/${step_id}/start-comission-fee-payment-paybox/`,
        method: 'post'
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          window.location.replace(data.data.payment_link)
        } catch (err) {
          console.log('err', err)
        }
      }
    }),
    // addSpouse: build.mutation<AddSpouseResponse, AddSpousePayload>({
    //   query: body => ({
    //     url: `/scoring/add-spouse/`,
    //     method: 'post',
    //     body
    //   })
    // }),
    addSpouse: build.mutation<AddSpouseResponse, AddSpousePayload>({
      query: ({ step_uuid, ...body }) => ({
        url: `/scoring/${step_uuid}/add-spouse-info`,
        method: 'post',
        body
      })
    }),
    startPledgeDocsSign: build.mutation<void, string>({
      query: flow_id => ({
        url: `/credits/${flow_id}/nca_to_step/`,
        method: 'post'
      })
    }),
    getDocumentHashToSign: build.query<GetSignDocsToStepResponse, string>({
      query: credit_app_id => ({
        url: `/credits/${credit_app_id}/get_sign_docs_to_step/`,
        method: 'get'
      })
    }),
    signDocsToStep: build.mutation<SignDocsToStepResponse, SignDocsToStepPayload>({
      query: ({ step_id, cms }) => ({
        url: `/credits/${step_id}/sign_docs_to_step/`,
        method: 'post',
        body: {
          client_ecp_signature: cms
        }
      })
    }),
    getInsuranceSum: build.mutation<GetInsuranceSumResponse, GetInsuranceSumPayload>({
      query: ({ amount, period }) => ({
        url: `/credits/calculate-insurance/`,
        method: 'post',
        body: {
          loan_amount: amount,
          period
        }
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            setInsuranseData({
              credit_sum: data.credit_sum,
              insurance_sum: data.insurance_sum
            })
          )
        } catch (error) {
          console.log(error)
        }
      }
    }),
    estimateRealEstate: build.mutation<EstimateRealEstateResponse, string>({
      query: flow_uuid => ({
        url: `/scoring/estimate-real-estate/${flow_uuid}/`,
        method: 'post'
      })
    }),
    estimateRealEstateRestart: build.mutation<EstimateRealEstateRestartResponse, string>({
      query: step_uuid => ({
        url: `/scoring/estimate-real-estate-restart/${step_uuid}/`,
        method: 'POST'
      })
    }),
    finishAcquaintanceStep: build.mutation<void, string>({
      query: step_uuid => ({
        url: `/credits/${step_uuid}/finish-acquaintance/`,
        method: 'POST'
      })
    })
  })
})

export const {
  useGetClientPropertiesQuery,
  useStartCheckingDocumentsMutation,
  useStartPayboxComissionPaymentMutation,
  useAddSpouseMutation,
  useGetCreditApplicationInfoQuery,
  useStartPledgeDocsSignMutation,
  useSignDocsToStepMutation,
  useGetInsuranceSumMutation,
  useGetDocsForPledgeQuery,
  useGetNotaryCitiesQuery,
  useGetNotariesQuery,
  useLazyGetNotariesQuery,
  useSetNotaryMutation,
  useEstimateRealEstateMutation,
  useEstimateRealEstateRestartMutation,
  useFetchClientPropertiesMutation,
  useGetDocumentHashToSignQuery,
  useFinishAcquaintanceStepMutation
} = pledgeApi
